import { useEffect, useRef, useState, startTransition } from "react";
import { Drawer, DrawerContent, DrawerTitle } from "@workspace/ui/components/ui/drawer";
import { BookingWidget } from "@/components/cars/BookingWidget";
import { VaulDrawerNestingContext } from "@/lib/drawer-nesting";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { useCurrency } from "@/i18n/CurrencyContext";
import { useFormatMoney } from "@/lib/formatMoney";
import { useTranslation } from "react-i18next";
import type { AvailabilityData, CarData, CarType, DeliveryAirport, ScarcitySignal } from "@/lib/api";
import { setLauncherVisibility } from "@/lib/intercom";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { Star, Zap } from "lucide-react";
import { publicCarOffer } from "@/lib/public-page-presentation";
import { useModalVisualViewport } from "@/hooks/useModalVisualViewport";
import { AGENT_FILL_EVENT, readAgentSelection, agentSelectionTimes, type AgentSelection } from "@/lib/agent-prefill";

interface MobileBookingBarProps {
  checkoutFlowId?: string;
  car: CarData;
  slug: string;
  isPreview?: boolean;
  dateFrom: string;
  dateTo: string;
  onDateFieldClick: () => void;
  deliveryAirports?: DeliveryAirport[];
  onRentalTypeChange?: (type: "driving" | "chauffeured" | "event") => void;
  ownerResponseRate: number | null;
  owner: {
    id?: string | null;
    first_name: string | null;
    last_name?: string | null;
    profile_image: string | null;
    city?: string | null;
  } | null;
  primaryImageUrl: string | null;
  /** Task #648: forwarded to BookingWidget for the Event-only Occasion picker. */
  eventTypes?: CarType[];
  /** Mobile-bug fix #1: forwarded so the inner BookingWidget can open
   *  an in-Drawer date picker on mobile. */
  availability?: AvailabilityData | null;
  onDateSelect?: (from: string, to: string) => void;
  /** Forwarded to BookingWidget for the back-from-checkout restore (#1699). */
  initialRentalType?: "driving" | "chauffeured" | "event";
  initialPickupTime?: string;
  initialDropoffTime?: string;
  initialNumPassengers?: number;
  initialOccasion?: string;
  initialAirportCode?: string | null;
  initialDeliveryDistance?: number | null;
  initialDeliveryAddress?: string;
  initialMessage?: string;
  /** Task #6806 — forwarded to BookingWidget for the scarcity banner. */
  scarcity?: ScarcitySignal | null;
}

export function MobileBookingBar(props: MobileBookingBarProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const drawerViewportStyle = useModalVisualViewport(open, false, "(max-width: 1023px)");
  const [agentSelection,setAgentSelection]=useState<AgentSelection|null>(null);
  const pendingAgentFill=useRef<any>(null);
  useEffect(()=>{
    const fill=(event:Event)=>{
      const detail=(event as CustomEvent).detail;
      if(detail?.carId!==Number(props.car.id) || detail.relay || !window.matchMedia("(max-width: 1023px)").matches)return;
      const selection=readAgentSelection(new URLSearchParams(detail.selection));
      if(!selection.hire || !selection.start || !selection.end)return;
      const supported=selection.hire==="driving" ? props.car.rental_driving_active :
        selection.hire==="chauffeured" ? props.car.rental_chauffeured_active : props.car.rental_event_active;
      if(!supported){detail.acknowledge?.(false);return;}
      pendingAgentFill.current=detail;setAgentSelection(selection);setOpen(true);
    };
    window.addEventListener(AGENT_FILL_EVENT,fill);
    return()=>window.removeEventListener(AGENT_FILL_EVENT,fill);
  },[props.car]);
  useEffect(()=>{
    if(!open || !agentSelection || !pendingAgentFill.current)return;
    const frame=requestAnimationFrame(()=>window.dispatchEvent(new CustomEvent(AGENT_FILL_EVENT,{
      detail:{...pendingAgentFill.current,relay:true},
    })));
    return()=>cancelAnimationFrame(frame);
  },[open,agentSelection]);
  const barRef = useRef<HTMLDivElement>(null);

  // Reserve the actual bar height, including wrapped translations and the
  // device safe area, rather than assuming every phone has the same footer.
  useEffect(() => {
    const bar = barRef.current;
    const page = bar?.closest<HTMLElement>(".car-journey");
    if (!bar || !page) return;
    const update = () => {
      const height = `${bar.getBoundingClientRect().height}px`;
      page.style.setProperty("--car-booking-bar-height", height);
      document.body.style.setProperty("--car-booking-bar-height", height);
    };
    document.body.classList.add("car-booking-bar-active");
    update();
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      page.style.removeProperty("--car-booking-bar-height");
      document.body.style.removeProperty("--car-booking-bar-height");
      document.body.classList.remove("car-booking-bar-active");
    };
  }, []);

  // Mobile-bug fix: while the reserve drawer is open, hide the Intercom
  // floating launcher so its bubble (z-index 2147483647) can't overlap the
  // drawer's sticky footer CTA. Mirrors ConversationPage. On phones the
  // launcher is already hidden (shouldHideDefaultLauncher: innerWidth < 768),
  // so the cleanup restore is a no-op there.
  useEffect(() => {
    if (!open) return;
    setLauncherVisibility(false);
    return () => {
      setLauncherVisibility(true);
    };
  }, [open]);

  const car = props.car;
  // Task #6387 — Instant Book emphasis on the mobile sticky bar. Page-level
  // signal: the car's stored `instant_rental` gated by the global
  // `feature_instant_book` kill-switch (same pattern as the detail-header
  // badge). Flag off ⇒ the bar renders exactly as a request-to-book car.
  const instantBookEnabled = useFeatureFlag("feature_instant_book");
  const isInstant = instantBookEnabled && !!car.instant_rental;
  const offer = publicCarOffer(car);
  const price = offer?.price ?? null;
  const currency = offer?.priceCurrency || car.currency_code;
  // Task #2342 — browsing surfaces render in renter's preferred currency.
  const { formatPreferred } = useCurrency();
  const { money: formatLocalisedMoney } = useFormatMoney();
  const priceLabel = price != null ? (formatPreferred(price, currency) ?? formatLocalisedMoney(price, currency)) : null;

  return (
    <>
      <div ref={barRef} className="car-mobile-booking-bar fixed inset-x-0 bottom-0 z-40 border-t border-border material-panel pb-safe lg:hidden">
        <div className="car-mobile-booking-inner mx-auto flex max-w-[1120px] items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {priceLabel ? (
                <p className="text-base font-semibold text-foreground">
                  {priceLabel}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    / {offer?.priceSpecification.unitCode === "HUR"
                      ? t("carDetail.hour", { defaultValue: "hour" })
                      : t("carDetail.day", { defaultValue: "day" })}
                  </span>
                </p>
              ) : (
                <p className="text-sm font-semibold text-foreground">
                  {t("carDetail.see_pricing", { defaultValue: "See pricing" })}
                </p>
              )}
              {isInstant && (
                <span
                  className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand"
                  data-testid="badge-bar-instant-book"
                >
                  <Zap className="h-3 w-3 shrink-0 fill-current" />
                  {t("carCard.social_proof.instant_book")}
                </span>
              )}
            </div>
            {car.reviews_rating != null && car.reviews_count != null && car.reviews_count > 0 ? (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3 fill-current text-foreground" />
                <span className="font-medium text-foreground">
                  {car.reviews_rating.toFixed(2)}
                </span>
                <span>· {car.reviews_count}</span>
              </p>
            ) : (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {[car.city, car.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              // INP: opening the Drawer mounts the full BookingWidget tree.
              // startTransition lets the browser paint the button press first.
              startTransition(() => setOpen(true));
            }}
            className="car-mobile-booking-cta primary-action touch-44 inline-flex items-center justify-center gap-1.5 rounded-xl px-6 py-3 text-sm font-semibold transition-colors"
            data-testid="button-mobile-bar-cta"
          >
            {isInstant && <Zap className="h-4 w-4 shrink-0 fill-current" />}
            {isInstant
              ? t("carDetail.book_instantly", { defaultValue: "Book instantly" })
              : t("carDetail.check_availability", {
                  defaultValue: "Check availability",
                })}
          </button>
        </div>
      </div>

      {/*
        Vaul repositions a drawer whenever an input gets focus by default. On
        iOS that turns this tall booking sheet into a short card at the top of
        the visual viewport, leaving the large white gap seen above the
        keyboard. This sheet keeps a stable geometry; BookingWidget controls
        only its own scroll panel when the address field is focused.
      */}
      <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
        <DrawerContent style={drawerViewportStyle} className="flex h-[92svh] max-h-[92svh] flex-col" data-testid="mobile-reserve-drawer">
          <DrawerTitle className="sr-only">
            {t("carDetail.book_this_car", { defaultValue: "Book this car" })}
          </DrawerTitle>
          <VaulDrawerNestingContext.Provider value>
          <div className="flex min-h-0 flex-1 flex-col">
            <SectionErrorBoundary name="BookingWidgetMobile">
              {/*
                Pass mobile explicitly rather than reading useIsMobile inside
                BookingWidget: the drawer host is itself lg:hidden, so the
                widget is unambiguously in drawer context whenever it renders
                here. This keeps the widget pure (no media-query coupling)
                and lets the same component still render as a sticky desktop
                sidebar elsewhere on the same page.
              */}
              <BookingWidget {...props} mobile onMobileClose={() => setOpen(false)}
                initialRentalType={agentSelection?.hire || props.initialRentalType}
                initialPickupTime={agentSelectionTimes(agentSelection || {}).pickup || props.initialPickupTime}
                initialDropoffTime={agentSelectionTimes(agentSelection || {}).dropoff || props.initialDropoffTime} />
            </SectionErrorBoundary>
          </div>
          </VaulDrawerNestingContext.Provider>
        </DrawerContent>
      </Drawer>
    </>
  );
}
